import {pickBy} from 'lodash';

import type {
  BasicUserSerializedType,
  CommentReactionsType,
  CommentReactionType,
  CommentReactionTypeWithTone,
  ReactionSkinNameType,
  ReactionSkinToneType,
} from 'sweet-types';

export const availableReactions: Array<{
  title: string;
  slug: CommentReactionType;
  icon: string;
}> = [
  {
    title: 'Thumbs up',
    slug: 'thumbsup',
    icon: '👍',
  },
  {
    title: 'Heart',
    slug: 'heart',
    icon: '❤️',
  },
  {
    title: 'Laugh',
    slug: 'smile',
    icon: '😄',
  },
  {
    title: 'Hooray',
    slug: 'tada',
    icon: '🎉',
  },
  {
    title: 'Eyes',
    slug: 'eyes',
    icon: '👀',
  },
  {
    title: 'Rocket',
    slug: 'rocket',
    icon: '🚀',
  },
];

export const skinToneableReactions = ['👍'];

// modifiers for skin tones on the Fitzpatrick scale
export const modifiers: ReactionSkinToneType[] = [
  {tone: '\u{1f3fb}', name: 'skin-tone-1-2'}, // (light)
  {tone: '\u{1f3fc}', name: 'skin-tone-3'},
  {tone: '\u{1f3fd}', name: 'skin-tone-4'},
  {tone: '\u{1f3fe}', name: 'skin-tone-5'},
  {tone: '\u{1f3ff}', name: 'skin-tone-6'}, // (dark)
];

// generate all tone enabled reactions as a list of reaction
export const availableTonedReaction: Array<{
  title: string;
  slug: CommentReactionTypeWithTone;
  type: CommentReactionType;
  icon: string;
  modifier: {
    name: ReactionSkinNameType | null;
    tone: string;
  };
}> = availableReactions
  .map((reaction) =>
    skinToneableReactions && skinToneableReactions.includes(reaction.icon)
      ? [{tone: '', name: null}, ...modifiers].map((modifier) =>
          !modifier.name
            ? {...reaction, type: reaction.slug, modifier}
            : {
                title: `${reaction.title} (${modifier.name})`,
                slug: `${reaction.slug}::${modifier.name}` as CommentReactionType,
                icon: `${reaction.icon}${modifier.tone}`,
                type: reaction.slug,
                modifier,
              }
        )
      : [{...reaction, type: reaction.slug, modifier: {name: null, tone: ''}}]
  )
  .reduce((acc, curVal) => {
    return acc.concat(curVal);
  }, []);

export function filterOutEmptyReactions(
  reactions: CommentReactionsType
): Partial<CommentReactionsType> {
  return pickBy(reactions, (users) => users.length > 0);
}

export function filterSkinToneReactions(reactions: CommentReactionsType): {
  modifier: {
    tone: string;
    name: ReactionSkinNameType | null;
  };
  users: BasicUserSerializedType[];
  title: string;
  slug: CommentReactionTypeWithTone;
  type: CommentReactionType;
  icon: string;
}[] {
  return availableTonedReaction
    .map((reaction) => ({
      ...reaction,
      users: reactions[reaction.slug],
    }))
    .filter((reactionData) => reactionData?.users?.length > 0);
}

// Map skin name to full modifier object.
export const skinNameToneMap: Record<string, ReactionSkinToneType> =
  Object.assign({}, ...modifiers.map((mod) => ({[mod.name]: mod})));

// removes previously existing skin tone modifiers from an emoji
export function removeModifier(emoji) {
  let ret = emoji;
  for (let i = 0; i < modifiers.length; i++) {
    ret = ret.replace(modifiers[i], '');
  }
  return ret;
}

export const modifyReaction = (icon: string, modifier?: string) => {
  return modifier && skinToneableReactions.includes(icon)
    ? removeModifier(icon) + modifier
    : icon;
};
